import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, Grid, ListItem } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

@Component({
    templateUrl: './phase-costs.component.html',
    styleUrls: ['./phase-costs.component.css'],
    standalone: true,
    imports: [
        WjInputModule,
        FormsModule,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        ActionToolbarCount,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        NgbNavOutlet,
    ],
})
export class PhaseCostsComponent extends BaseComponent {
    faSearch = faSearch
    grid: Grid<any>
    rawGrid: Grid<any>
    ratioGrid: Grid<any>
    showSummary = true
    active = 1

    projectTypes: any[] = []
    costTypes: any[] = []
    jobs: any[] = []
    phases: any[] = []
    activities: any[] = []

    projectTypesMap: ListItem<string>[] = []

    jobStatusList: ListItem<number>[] = [
        new ListItem(1, 'Active'),
        new ListItem(2, 'Soft Closed'),
        new ListItem(3, 'Hard Closed'),
        new ListItem(0, 'Pending'),
    ]

    costTypesMap: ListItem<number>[] = []
    jobsMap: ListItem<number>[] = []
    phasesMap: ListItem<string>[] = []
    activitiesMap: ListItem<string>[] = []
    phaseTypeMap = [new ListItem(0, 'Phases are By Phase'), new ListItem(1, 'Phases are By Activity')]
    reportGroup = [new ListItem(0, 'Group by Job'), new ListItem(1, 'Group by Phase')]

    selectedProjectTypes: any
    selectedJobStatus: any
    selectedCostTypes: any
    selectedJobs: any
    selectedPhases: any
    selectedPhaseType = 0
    selectedReportGroup = 0
    years = 5
    minCost = 0
    maxCost = 999999999

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsEdit])
    }

    constructor() {
        super()
        this.grid = new Grid<any>(this.services).wrapColumnHeaders()

        this.rawGrid = new Grid<any>(this.services).wrapColumnHeaders().autoGenerateColumns(true)

        this.ratioGrid = new Grid<any>(this.services).wrapColumnHeaders().autoGenerateColumns(true)

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('/api/VpData/QueryProjectTypes'),
                    this.services.data.get('/api/VpData/QueryCostTypes'),
                    this.services.data.get('/api/VpData/InitPhaseCostCache'),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.projectTypes = result[0]
                        this.costTypes = result[1]
                        this.projectTypesMap = this.projectTypes.map(x => new ListItem(x.databaseValue, x.databaseValue))
                        this.costTypesMap = this.costTypes.map(x => new ListItem(x.costType, x.costType + ' - ' + x.description))
                    },
                }),
        )
    }

    onProjectTypesChanged() {
        this.clearGrids()
        const previousJobValues = this.selectedJobs?.map(x => x.value) ?? []
        const dto = {
            projectTypes: this.selectedProjectTypes.map(x => x.value),
            jobStatus: this.selectedJobStatus.map(x => x.value),
        }
        const params = new HttpParams()
            .set('years', this.years ? this.years : 5)
            .set('minCost', this.minCost)
            .set('maxCost', this.maxCost)
        this.cancelableRequest(
            'jobs',
            true,
            this.services.data
                .load(true, true, this.services.data.post('/api/VpData/QueryJobsByProjectAndStatusType', dto, params))
                .subscribe({
                    next: result => {
                        this.jobs = result[0]
                        this.jobsMap = this.jobs.map(x => new ListItem(x.job, x.job + ' - ' + x.mailAddress))

                        const validJobValues = new Set(this.jobsMap.map(x => x.value))
                        this.selectedJobs = previousJobValues
                            .filter(v => validJobValues.has(v))
                            .map(v => this.jobsMap.find(x => x.value === v))
                            .filter((x): x is ListItem<number> => x != null)

                        if (this.selectedJobs.length > 0) {
                            this.onJobsChanged()
                        }
                    },
                }),
        )
    }

    onJobsChanged() {
        this.clearGrids()
        const previousPhaseValues = this.selectedPhases?.map(x => x.value) ?? []
        const jobs = (this.selectedJobs?.map(x => x.value) ?? []) as string[]
        this.cancelableRequest(
            'phases',
            true,
            this.services.data.load(true, true, this.services.data.post('/api/VpData/QueryDistinctPhasesByJobs', jobs)).subscribe({
                next: result => {
                    this.phases = result[0].phases
                    this.activities = result[0].activities
                    this.phasesMap = this.phases.map(x => new ListItem(x, x))
                    this.activitiesMap = this.activities.map(x => new ListItem(x, x))

                    const activeMap = this.selectedPhaseType === 0 ? this.phasesMap : this.activitiesMap
                    const validPhaseValues = new Set(activeMap.map(x => x.value))
                    this.selectedPhases = previousPhaseValues
                        .filter(v => validPhaseValues.has(v))
                        .map(v => activeMap.find(x => x.value === v))
                        .filter((x): x is ListItem<string> => x != null)
                },
            }),
        )
    }

    onPhasesChanged() {
        this.clearGrids()
    }

    getPhaseCostData() {
        this.showSummary = true
        this.queryData()
    }

    getRawPhaseCostData() {
        this.showSummary = false
        this.queryData()
    }

    queryData() {
        this.grid.items = []
        this.rawGrid.items = []

        const jobs = this.selectedJobs.map(x => x.value)
        const phases = this.selectedPhases.map(x => x.value)
        const costTypes = this.selectedCostTypes.map(x => x.value)
        const dto = {
            jobs: jobs,
            phases: phases,
            costTypes: costTypes,
            isPhaseCodes: this.selectedPhaseType === 0,
            isGroupedByJob: this.selectedReportGroup === 0,
        }

        this.cancelableRequest(
            'costData',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.post(this.showSummary ? '/api/VpData/QueryCosts' : '/api/VpData/QueryRawCostData', dto),
                )
                .subscribe({
                    next: result => {
                        if (this.showSummary) this.grid.items = result[0].phaseJobCostDtos
                        else this.rawGrid.items = result[0]

                        this.rawGrid.refresh()
                        this.ratioGrid.items = result[0].phaseJobCostByCostTypeDtos
                    },
                }),
        )
    }

    clearGrids() {
        this.grid.items = []
        this.rawGrid.items = []
        this.ratioGrid.items = []
    }

    itemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem

            cell.classList.remove('yellow-cell')
            cell.classList.remove('green-cell')

            if (col.binding === 'hoursPercentUsed' && dataItem.estHours === 0 && dataItem.actualHours > 0) {
                cell.innerHTML = 'Divide by 0'
            }
            if (col.binding === 'costPercentUsed' && dataItem.estCost === 0 && dataItem.actualCost > 0) {
                cell.innerHTML = 'Divide by 0'
            }

            if (
                col.binding === 'actualHours' ||
                col.binding === 'estHours' ||
                col.binding === 'hoursVariance' ||
                col.binding === 'hoursPercentUsed'
            ) {
                cell.classList.add('yellow-cell')
            }

            if (
                col.binding === 'actualCost' ||
                col.binding === 'estCost' ||
                col.binding === 'costVariance' ||
                col.binding === 'costPercentUsed'
            ) {
                cell.classList.add('green-cell')
            }
        }
    }
}
